<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import axios from 'axios';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import Divider from 'primevue/divider';
import Message from 'primevue/message';
import Checkbox from 'primevue/checkbox';
import { useToast } from 'primevue/usetoast';

const props = defineProps({
    visible: {
        type: Boolean,
        default: false,
    },
    initialUrl: {
        type: String,
        default: '',
    },
    siteExists: {
        type: Boolean,
        default: false,
    },
    siteId: {
        type: Number,
        default: null,
    },
    siteDa: {
        type: Number,
        default: null,
    },
    siteDr: {
        type: Number,
        default: null,
    },
    currency: {
        type: String,
        default: 'USD',
    },
    context: {
        type: String,
        default: 'budget', // 'budget' | 'order'
    },
});

const emit = defineEmits(['update:visible', 'created']);

const submitLabel = 'Register Offer';

const toast = useToast();
const loading = ref(false);
const formDataLoading = ref(false);

const sellers = ref([]);
const types = ref([]);
const exchangeData = ref({});

const form = ref({
    url: '',
    seller_id: null,
    selected_types: [],
    create_site: true,
    da: 0,
    dr: 0,
    link_type: 'DOFOLLOW',
    type_prices: {},
    type_enabled: {},
});

const errors = ref({});

const linkTypeOptions = [
    { label: 'DoFollow', value: 'DOFOLLOW' },
    { label: 'NoFollow', value: 'NOFOLLOW' },
    { label: 'Sponsored', value: 'SPONSORED' },
];

const sellerOptions = computed(() => {
    return sellers.value.map(s => ({
        label: s.name + (s.linkarme_seller ? ' (Linkarme)' : ''),
        value: s.id,
        linkarme_seller: s.linkarme_seller,
    }));
});

const typeOptions = computed(() => {
    return types.value.map(t => ({
        label: t.name,
        value: t.id,
    }));
});

const selectedSeller = computed(() => {
    return sellers.value.find(s => s.id === form.value.seller_id) || null;
});

const isLinkarmeSeller = computed(() => {
    return selectedSeller.value?.linkarme_seller === true || selectedSeller.value?.linkarme_seller === 1;
});

// Get enabled types only
const enabledTypes = computed(() => {
    return form.value.selected_types.filter(typeId => form.value.type_enabled[typeId] !== false);
});

const loadFormData = async () => {
    formDataLoading.value = true;
    try {
        const response = await axios.get(route('quick-offer.form-data'));
        sellers.value = response.data.sellers || [];
        types.value = response.data.types || [];
        exchangeData.value = response.data.exchangeData || {};
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load form data',
            life: 3000,
        });
    } finally {
        formDataLoading.value = false;
    }
};

// Convert currency using exchange rates
const convertCurrency = (amount, fromCurrency, toCurrency) => {
    if (!exchangeData.value || fromCurrency === toCurrency) {
        return amount;
    }

    const fromExchange = exchangeData.value[fromCurrency?.toUpperCase()];
    const toExchange = exchangeData.value[toCurrency?.toUpperCase()];

    if (!fromExchange || !toExchange) {
        return amount;
    }

    const fromRate = (fromExchange.value || 100) / 100;
    const toRate = (toExchange.value || 100) / 100;

    const usdAmount = amount / fromRate;
    return usdAmount * toRate;
};

// Calculate suggested price based on cost and exchange formula
const calculateSuggestedPrice = (cost, costCurrency, saleCurrency) => {
    if (!exchangeData.value || !cost || cost <= 0) {
        return 0;
    }

    const saleCurrencyUpper = saleCurrency?.toUpperCase() || props.currency;
    const exchangeInfo = exchangeData.value[saleCurrencyUpper];

    if (!exchangeInfo) {
        return cost;
    }

    // Convert cost to sale currency
    const convertedCost = convertCurrency(cost, costCurrency, saleCurrencyUpper);

    // Get exchange parameters
    const fixedAddition = (exchangeInfo.fixed_addition || 0) / 100;
    const percentageAddition = (exchangeInfo.percentage_addition || 0) / 10000;
    const applyPercentageToTotal = exchangeInfo.apply_percentage_to_total || false;

    let suggestedPrice;

    if (applyPercentageToTotal) {
        const baseAmount = convertedCost + fixedAddition;
        suggestedPrice = baseAmount * (1 + percentageAddition);
    } else {
        const percentageAmount = convertedCost * percentageAddition;
        suggestedPrice = convertedCost + percentageAmount + fixedAddition;
    }

    return Math.round(suggestedPrice * 100) / 100;
};

// Get suggested price for a type
const getSuggestedPriceForType = (typeId) => {
    const prices = form.value.type_prices[typeId];
    if (!prices || !prices.cost || prices.cost <= 0) {
        return null;
    }
    return calculateSuggestedPrice(prices.cost, props.currency, props.currency);
};

// Apply recommended price to sale
const useRecommendedPrice = (typeId) => {
    const suggested = getSuggestedPriceForType(typeId);
    if (suggested) {
        form.value.type_prices[typeId].sale = suggested;
    }
};

// Format currency for display
const formatCurrency = (value, currency) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || props.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

watch(() => props.visible, (newVal) => {
    if (newVal) {
        form.value = {
            url: props.initialUrl || '',
            seller_id: null,
            selected_types: [],
            create_site: !props.siteExists,
            da: props.siteDa || 0,
            dr: props.siteDr || 0,
            link_type: 'DOFOLLOW',
            type_prices: {},
            type_enabled: {},
        };
        errors.value = {};

        if (sellers.value.length === 0) {
            loadFormData();
        }
    }
});

watch(() => props.initialUrl, (newVal) => {
    if (props.visible) {
        form.value.url = newVal || '';
    }
});

// Initialize type prices and enabled state when types are selected
watch(() => form.value.selected_types, (newTypes) => {
    newTypes.forEach(typeId => {
        if (!form.value.type_prices[typeId]) {
            form.value.type_prices[typeId] = {
                cost: 0,
                sale: 0,
            };
        }
        if (form.value.type_enabled[typeId] === undefined) {
            form.value.type_enabled[typeId] = true;
        }
    });
}, { deep: true });

// Update sale price when cost changes for external sellers
watch(() => form.value.type_prices, (newPrices) => {
    if (!isLinkarmeSeller.value) {
        // For external sellers, auto-calculate sale price
        Object.keys(newPrices).forEach(typeId => {
            const suggested = getSuggestedPriceForType(parseInt(typeId));
            if (suggested !== null && suggested > 0) {
                newPrices[typeId].sale = suggested;
            }
        });
    }
}, { deep: true });

const getTypeName = (typeId) => {
    const type = types.value.find(t => t.id === typeId);
    return type?.name || '';
};

const validate = () => {
    errors.value = {};

    if (!form.value.url || !form.value.url.trim()) {
        errors.value.url = 'URL is required';
    }

    if (!form.value.seller_id) {
        errors.value.seller_id = 'Seller is required';
    }

    if (enabledTypes.value.length === 0) {
        errors.value.types = 'At least one niche must be enabled';
    }

    // Validate each enabled type has a cost
    enabledTypes.value.forEach(typeId => {
        const prices = form.value.type_prices[typeId];
        if (!prices || prices.cost <= 0) {
            errors.value[`type_${typeId}_cost`] = 'Cost is required';
        }
        // For Linkarme sellers, validate sale price
        if (isLinkarmeSeller.value && (!prices || prices.sale <= 0)) {
            errors.value[`type_${typeId}_sale`] = 'Sale value is required';
        }
    });

    return Object.keys(errors.value).length === 0;
};

const handleSubmit = async () => {
    if (!validate()) return;

    loading.value = true;

    const typesPayload = enabledTypes.value.map(typeId => {
        const prices = form.value.type_prices[typeId] || { cost: 0, sale: 0 };
        // For external sellers, calculate the sale price from cost
        let salePrice = prices.sale;
        if (!isLinkarmeSeller.value) {
            const suggested = getSuggestedPriceForType(typeId);
            salePrice = suggested || prices.cost;
        }
        return {
            type_id: typeId,
            type_name: getTypeName(typeId),
            cost: Math.round(prices.cost * 100),
            cost_coin: props.currency,
            sale: Math.round(salePrice * 100),
            sale_coin: props.currency,
        };
    });

    try {
        const response = await axios.post(route('quick-offer.store'), {
            url: form.value.url.trim(),
            seller_id: form.value.seller_id,
            types: typesPayload,
            create_site: form.value.create_site,
            da: form.value.da,
            dr: form.value.dr,
            link_type: form.value.link_type,
        });

        if (response.data.success) {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: response.data.message || 'Site and offers created successfully',
                life: 3000,
            });

            // Emit created event with site and offer data for adding to budget
            emit('created', {
                site: response.data.site,
                offers: response.data.offers,
                seller: selectedSeller.value,
                typesPayload: typesPayload,
            });

            emit('update:visible', false);
        }
    } catch (error) {
        const message = error.response?.data?.message || 'Failed to create site/offers';
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: message,
            life: 5000,
        });

        if (error.response?.data?.errors) {
            errors.value = error.response.data.errors;
        }
    } finally {
        loading.value = false;
    }
};

const handleClose = () => {
    emit('update:visible', false);
};

onMounted(() => {
    if (sellers.value.length === 0) {
        loadFormData();
    }
});
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="$emit('update:visible', $event)"
        :header="$t('Quick Register Offer')"
        :modal="true"
        :style="{ width: '650px' }"
        :closable="!loading"
        :contentStyle="{ overflow: 'auto', maxHeight: '75vh' }"
    >
        <div v-if="formDataLoading" class="flex justify-center py-8">
            <i class="pi pi-spin pi-spinner text-4xl"></i>
        </div>

        <div v-else class="flex flex-col gap-4">
            <!-- Info message -->
            <Message v-if="!siteExists" severity="info" :closable="false" class="m-0">
                {{ $t('This site does not exist. It will be created along with the offer.') }}
            </Message>

            <Message v-else severity="success" :closable="false" class="m-0">
                {{ $t('Site exists. A new offer will be added.') }}
            </Message>

            <!-- URL -->
            <div>
                <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('URL') }}</label>
                <InputText
                    v-model="form.url"
                    class="w-full"
                    :class="{ 'p-invalid': errors.url }"
                    :disabled="siteExists"
                />
                <small v-if="errors.url" class="p-error">{{ errors.url }}</small>
            </div>

            <!-- Site details (only if creating new site) -->
            <div v-if="!siteExists && form.create_site" class="grid grid-cols-3 gap-3">
                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-1">DA</label>
                    <InputNumber
                        v-model="form.da"
                        fluid
                        :min="0"
                        :max="100"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-1">DR</label>
                    <InputNumber
                        v-model="form.dr"
                        fluid
                        :min="0"
                        :max="100"
                    />
                </div>

                <div>
                    <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Link Type') }}</label>
                    <Select
                        v-model="form.link_type"
                        :options="linkTypeOptions"
                        optionLabel="label"
                        optionValue="value"
                        fluid
                    />
                </div>
            </div>

            <Divider class="my-0" />

            <!-- Seller -->
            <div>
                <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Seller') }} <span class="text-red-500">*</span></label>
                <Select
                    v-model="form.seller_id"
                    :options="sellerOptions"
                    optionLabel="label"
                    optionValue="value"
                    :placeholder="$t('Select seller')"
                    fluid
                    :class="{ 'p-invalid': errors.seller_id }"
                    filter
                />
                <small v-if="errors.seller_id" class="p-error">{{ errors.seller_id }}</small>
            </div>

            <!-- Types/Niches -->
            <div>
                <label class="block text-sm font-medium text-surface-700 mb-1">{{ $t('Niches') }} <span class="text-red-500">*</span></label>
                <MultiSelect
                    v-model="form.selected_types"
                    :options="typeOptions"
                    optionLabel="label"
                    optionValue="value"
                    :placeholder="$t('Select niches')"
                    fluid
                    :class="{ 'p-invalid': errors.types }"
                    filter
                    display="chip"
                />
                <small v-if="errors.types" class="p-error">{{ errors.types }}</small>
            </div>

            <!-- Prices per type -->
            <div v-if="form.selected_types.length > 0" class="flex flex-col gap-3">
                <Divider class="my-0" />
                <div class="flex items-center justify-between">
                    <span class="font-semibold text-surface-700 text-sm">{{ $t('Prices per Niche') }}</span>
                    <small class="text-surface-500">{{ enabledTypes.length }} {{ $t('selected') }}</small>
                </div>

                <!-- Compact rows -->
                <div
                    v-for="typeId in form.selected_types"
                    :key="typeId"
                    class="p-3 rounded border border-surface-200"
                    :class="form.type_enabled[typeId] === false ? 'bg-surface-50 opacity-60' : 'bg-white'"
                >
                    <!-- Row header: checkbox + niche name -->
                    <div class="flex items-center gap-2 mb-2">
                        <Checkbox
                            v-model="form.type_enabled[typeId]"
                            :binary="true"
                            :inputId="`enable_${typeId}`"
                        />
                        <label :for="`enable_${typeId}`" class="font-medium text-sm cursor-pointer">
                            {{ getTypeName(typeId) }}
                        </label>
                    </div>

                    <!-- Price inputs row -->
                    <div class="grid grid-cols-2 gap-3">
                        <!-- Cost -->
                        <div>
                            <label class="block text-xs text-surface-500 mb-1">{{ $t('Cost') }}</label>
                            <InputNumber
                                v-model="form.type_prices[typeId].cost"
                                mode="currency"
                                :currency="currency"
                                locale="en-US"
                                fluid
                                :class="{ 'p-invalid': errors[`type_${typeId}_cost`] }"
                                :min="0"
                                :disabled="form.type_enabled[typeId] === false"
                            />
                        </div>

                        <!-- Sale -->
                        <div>
                            <label class="block text-xs text-surface-500 mb-1">{{ $t('Sale') }}</label>
                            <InputNumber
                                v-if="isLinkarmeSeller"
                                v-model="form.type_prices[typeId].sale"
                                mode="currency"
                                :currency="currency"
                                locale="en-US"
                                fluid
                                :class="{ 'p-invalid': errors[`type_${typeId}_sale`] }"
                                :min="0"
                                :disabled="form.type_enabled[typeId] === false"
                            />
                            <div v-else class="flex items-center px-3 py-2 bg-surface-100 border border-surface-200 rounded text-sm text-surface-700 min-h-[38px]">
                                {{ getSuggestedPriceForType(typeId) ? formatCurrency(getSuggestedPriceForType(typeId), currency) : '-' }}
                            </div>
                            <!-- Use recommended link (Linkarme only) -->
                            <a
                                v-if="isLinkarmeSeller && getSuggestedPriceForType(typeId) && form.type_enabled[typeId] !== false"
                                href="#"
                                class="text-xs text-blue-600 hover:text-blue-800 hover:underline mt-1 inline-block"
                                @click.prevent="useRecommendedPrice(typeId)"
                            >
                                {{ $t('Use recommended') }}: {{ formatCurrency(getSuggestedPriceForType(typeId), currency) }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-end gap-3">
                <Button
                    :label="$t('Cancel')"
                    icon="pi pi-times"
                    severity="secondary"
                    size="small"
                    @click="handleClose"
                    :disabled="loading"
                />
                <Button
                    :label="$t(submitLabel)"
                    icon="pi pi-check"
                    size="small"
                    @click="handleSubmit"
                    :loading="loading"
                    :disabled="formDataLoading || enabledTypes.length === 0"
                />
            </div>
        </template>
    </Dialog>
</template>
